/**
 * SalesReportScreen — date-range sales summary with payment-method breakdown,
 * top products, and hourly distribution.
 *
 * Data is computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadSalesRange } from '@/reports/sales/salesDataSource';
import { buildSalesSummary, type SalesSummary as SalesData } from '@/reports/sales/salesSummary';
import { escapeHtml } from '@/utils/escapeHtml';

// ─── Component ───────────────────────────────────────────────────────────────

export function SalesReportScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('today');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<SalesData | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const source = await loadSalesRange(activeStoreId, from, to);
      setData(buildSalesSummary(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExport = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      const html = buildSalesHtml(data, from, to);
      await pdf.saveFromHtml({
        html,
        // `to` is exclusive; name the file after the last day actually covered.
        defaultFileName: `SalesReport_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.topProducts, [
        { header: 'Product', value: (p) => p.name },
        { header: 'Quantity', value: (p) => p.quantity },
        { header: 'Revenue', value: (p) => p.revenue },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `SalesReport_TopProducts_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const maxHourly = data ? Math.max(...data.hourly, 1) : 1;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}
          >
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.topProducts.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExport()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Net revenue" value={formatCurrency(data.totalSales)} accent />
            <KPI label="Invoices" value={String(data.invoiceCount)} />
            <KPI label="Avg. invoice" value={formatCurrency(data.avgSaleValue)} />
            <KPI label="Net tax" value={formatCurrency(data.totalTax)} />
          </div>

          {/* Payment breakdown + credit */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Card title="Payment breakdown">
              <PayRow label="Cash" amount={data.cashSales} />
              <PayRow label="UPI"  amount={data.upiSales} />
              <PayRow label="Card" amount={data.cardSales} />
              {/* Signed: a refund on a non-standard method can net negative. */}
              {data.otherSales !== 0 && <PayRow label="Other" amount={data.otherSales} />}
              {data.creditDue  > 0 && <PayRow label="Credit due" amount={data.creditDue} warn />}
              {data.refunds > 0 && (
                <>
                  <div className="my-2 border-t border-stroke-hairline/10" />
                  <PayRow label="Gross sales" amount={data.grossSales} />
                  <PayRow label={`Returns (${data.creditNoteCount} credit note${data.creditNoteCount !== 1 ? 's' : ''})`} amount={-data.refunds} warn />
                </>
              )}
            </Card>

            <Card title="Peak hours">
              <div className="flex h-24 items-end gap-px overflow-hidden">
                {data.hourly.map((v, h) => (
                  <div key={h} className="flex flex-1 flex-col items-center gap-0.5">
                    <div
                      className="w-full rounded-t bg-accent/60 transition-all"
                      style={{ height: `${Math.round((v / maxHourly) * 88)}px`, minHeight: v > 0 ? 2 : 0 }}
                      title={`${h}:00 — ${formatCurrency(v)}`}
                    />
                    {h % 6 === 0 && (
                      <span className="text-label text-fg-tertiary">{h}</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Top products */}
          {data.topProducts.length > 0 && (
            <Card title="Top products by revenue">
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Product</th>
                    <th className="pb-1 text-right font-normal">Qty</th>
                    <th className="pb-1 text-right font-normal">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topProducts.map((p, i) => (
                    <tr key={i} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-primary">{p.name}</td>
                      <td className="py-1.5 text-right text-fg-secondary tabular-nums">{p.quantity}</td>
                      <td className="py-1.5 text-right text-fg-primary tabular-nums">{formatCurrency(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

function PayRow({ label, amount, warn }: { label: string; amount: number; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={cn('text-body', warn ? 'text-warning-ink' : 'text-fg-secondary')}>{label}</span>
      <span className={cn('text-body tabular-nums', warn ? 'text-warning-ink' : 'text-fg-primary')}>
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildSalesHtml(data: SalesData, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (d: Date) => d.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const rows = data.topProducts.map((p) =>
    `<tr><td>${escapeHtml(p.name)}</td><td style="text-align:right">${p.quantity}</td><td style="text-align:right">${fmt(p.revenue)}</td></tr>`
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px}h2{font-size:14px;margin-top:24px;text-transform:uppercase;color:#666;border-bottom:1px solid #eee;padding-bottom:4px}
table{width:100%;border-collapse:collapse;font-size:13px}th,td{padding:6px 8px;border-bottom:1px solid #eee}th{text-align:left;color:#666;font-weight:500}
.kpi{display:inline-block;min-width:140px;margin:8px 16px 8px 0}
.kpi-label{font-size:11px;text-transform:uppercase;color:#888}.kpi-value{font-size:20px;font-weight:700;color:#4F46E5}</style></head>
<body>
<h1>Sales Report</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)}</p>
<div>
  <div class="kpi"><div class="kpi-label">Net Revenue</div><div class="kpi-value">${fmt(data.totalSales)}</div></div>
  <div class="kpi"><div class="kpi-label">Invoices</div><div class="kpi-value">${data.invoiceCount}</div></div>
  <div class="kpi"><div class="kpi-label">Avg Invoice</div><div class="kpi-value">${fmt(data.avgSaleValue)}</div></div>
  <div class="kpi"><div class="kpi-label">Net Tax</div><div class="kpi-value">${fmt(data.totalTax)}</div></div>
</div>
${data.refunds > 0 ? `<h2>Returns</h2>
<table><tr><th>Item</th><th style="text-align:right">Amount</th></tr>
<tr><td>Gross sales</td><td style="text-align:right">${fmt(data.grossSales)}</td></tr>
<tr><td>Less: returns (${data.creditNoteCount} credit note${data.creditNoteCount !== 1 ? 's' : ''})</td><td style="text-align:right">-${fmt(data.refunds)}</td></tr>
<tr><td><b>Net revenue</b></td><td style="text-align:right"><b>${fmt(data.totalSales)}</b></td></tr>
</table>` : ''}
<h2>Payment Breakdown${data.refunds > 0 ? ' (net of refunds)' : ''}</h2>
<table><tr><th>Method</th><th style="text-align:right">Amount</th></tr>
<tr><td>Cash</td><td style="text-align:right">${fmt(data.cashSales)}</td></tr>
<tr><td>UPI</td><td style="text-align:right">${fmt(data.upiSales)}</td></tr>
<tr><td>Card</td><td style="text-align:right">${fmt(data.cardSales)}</td></tr>
${data.otherSales !== 0 ? `<tr><td>Other</td><td style="text-align:right">${fmt(data.otherSales)}</td></tr>` : ''}
${data.creditDue > 0 ? `<tr><td>Credit Due</td><td style="text-align:right">${fmt(data.creditDue)}</td></tr>` : ''}
</table>
<h2>Top Products</h2>
<table><tr><th>Product</th><th style="text-align:right">Qty</th><th style="text-align:right">Revenue</th></tr>${rows}</table>
<p style="margin-top:40px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
